import { useState, useEffect } from 'react';
import { WeeklyMenuResponse, DayOfWeek, MealType } from '@/types';
import { weeklyMenuService } from '@/services/weeklyMenuService';
import { useAuth } from '@/contexts/AuthContext';
import { getCorrectImageUrl } from '@/utils/imageUtils'; // ✅ IMPORTAR


export const useWeeklyMenu = () => {
    const [currentMenu, setCurrentMenu] = useState<WeeklyMenuResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { token, isLoggedIn } = useAuth();

    // Función para procesar las imágenes del menú
    const processMenuImages = (menu: WeeklyMenuResponse): WeeklyMenuResponse => {
        if (!menu.dayMeals) return menu;

        // Crear una copia profunda para no mutar el original
        const processedMenu = { ...menu, dayMeals: { ...menu.dayMeals } };

        // Procesar imágenes en todas las recetas del menú
        Object.entries(processedMenu.dayMeals).forEach(([day, dayMeals]) => {
            if (dayMeals) {
                Object.entries(dayMeals).forEach(([mealType, recipes]) => {
                    if (Array.isArray(recipes)) {
                        recipes.forEach(recipe => {
                            if (recipe.image) {
                                recipe.image = getCorrectImageUrl(recipe.image);
                            }
                        });
                    }
                });
            }
        });

        return processedMenu;
    };

    // Cargar menú de la semana actual
    const loadCurrentWeekMenu = async () => {
        if (!isLoggedIn || !token) {
            setCurrentMenu(null);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const menu = await weeklyMenuService.getCurrentWeekMenu(token);
            setCurrentMenu(menu);
            console.log('✅ Current week menu loaded:', menu);
        } catch (err: any) {
            setError(err.message || 'Error loading weekly menu');
            console.error('Error loading weekly menu:', err);
        } finally {
            setLoading(false);
        }
    };

    // Agregar receta al menú
    const addRecipeToMenu = async (
        dayOfWeek: DayOfWeek,
        mealType: MealType,
        recipeId: number
    ): Promise<boolean> => {
        if (!isLoggedIn || !token || !currentMenu) return false;

        try {
            setLoading(true);
            const updatedMenu = await weeklyMenuService.addRecipeToMenu(
                currentMenu.id,
                dayOfWeek,
                mealType,
                recipeId,
                token
            );
            const processedMenu = processMenuImages(updatedMenu); // ✅ PROCESAR IMÁGENES
            setCurrentMenu(processedMenu);
            console.log('✅ Recipe added to menu');
            return true;
        } catch (err: any) {
            console.error('Error adding recipe to menu:', err);
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Eliminar receta del menú
    const removeRecipeFromMenu = async (
        dayOfWeek: DayOfWeek,
        mealType: MealType
    ): Promise<boolean> => {
        if (!isLoggedIn || !token || !currentMenu) {
            console.error('❌ Cannot remove recipe: missing auth or menu');
            return false;
        }

        try {
            setLoading(true);
            console.log(`🗑️ Removing recipe from ${dayOfWeek}.${mealType}`);

            const updatedMenu = await weeklyMenuService.removeRecipeFromMenu(
                currentMenu.id,
                dayOfWeek,
                mealType,
                token
            );

            const processedMenu = processMenuImages(updatedMenu);
            setCurrentMenu(processedMenu);

            console.log('✅ Recipe removed from menu successfully');
            return true;
        } catch (err: any) {
            console.error('❌ Error removing recipe from menu:', err);
            // Recargar el menú para sincronizar con el backend
            await loadCurrentWeekMenu();
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Obtener receta de un slot específico
    const getRecipeInSlot = (dayOfWeek: DayOfWeek, mealType: MealType) => {
        if (!currentMenu?.dayMeals) return null;

        const dayMeals = currentMenu.dayMeals[dayOfWeek];
        if (!dayMeals) return null;

        const meals = dayMeals[mealType];
        return meals && meals.length > 0 ? meals[0] : null;
    };

    // Cargar menú al montar el hook o cuando cambie el login
    useEffect(() => {
        loadCurrentWeekMenu();
    }, [isLoggedIn, token]);

    return {
        currentMenu,
        loading,
        error,
        addRecipeToMenu,
        removeRecipeFromMenu,
        getRecipeInSlot,
        refetch: loadCurrentWeekMenu
    };
};